import React, { useState } from 'react';
import api from '../../core/api';
import type { MarketingLead } from './types';

interface Props {
    lead: MarketingLead;
    onClose: () => void;
    onSent?: (to: string) => void;
}

// Helper to extract email from text if not in lead.email
function extractEmail(lead: MarketingLead): string {
    if (lead.email && lead.email.includes('@')) return lead.email;
    const combined = `${lead.followupNotes || ''} ${lead.leadSummary || ''}`;
    const match = combined.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    return match ? match[0] : '';
}

// Helper to extract doctor or decision maker name
function extractDoctorName(lead: MarketingLead): string {
    if (lead.name && (lead.name.toLowerCase().includes('dr') || lead.name.length > 3)) {
        return lead.name;
    }
    const combined = `${lead.followupNotes || ''} ${lead.leadSummary || ''} ${lead.clinicName || ''}`;
    const match = combined.match(/(?:Dra?\.|Doctora?)\s+([A-Za-zÁÉÍÓÚáéíóúñÑ]+(?:\s+[A-Za-zÁÉÍÓÚáéíóúñÑ]+)?)/i);
    if (match) return match[0];
    if (lead.clinicName && /dr|dra/i.test(lead.clinicName)) return lead.clinicName;
    return 'Doctor(a) y Equipo Directivo';
}

// Helper to extract whether proposal email was already sent
export function extractProposalSentInfo(lead: MarketingLead): { sent: boolean; details?: string; date?: string; to?: string } {
    const text = `${lead.followupNotes || ''} ${lead.leadSummary || ''}`;
    const isSent = /propuesta\s+(?:enviada|por\s+correo|remitida)|correo\s+enviado|email\s+enviado|✉️\s*propuesta/i.test(text);
    if (!isSent) return { sent: false };

    const matchWithTo = text.match(/✉️\s*Propuesta enviada por correo a\s+([^\s(]+)(?:\s*\(([^)]+)\))?/i)
                     || text.match(/Propuesta enviada\s+(?:por correo\s+)?a\s+([^\s(]+)(?:\s*\(([^)]+)\))?/i);

    if (matchWithTo) {
        return {
            sent: true,
            to: matchWithTo[1],
            date: matchWithTo[2],
            details: `Enviado a ${matchWithTo[1]}${matchWithTo[2] ? ` (${matchWithTo[2]})` : ''}`,
        };
    }

    return {
        sent: true,
        to: lead.email,
        details: 'Propuesta enviada por correo anteriormente',
    };
}

type TemplateKey = 'auditoria' | 'ejecutivo' | 'activacion';

interface TemplateOption {
    id: TemplateKey;
    title: string;
    badge: string;
    getSubject: (clinic: string, doctor: string) => string;
    getBody: (clinic: string, doctor: string, volume?: string) => string;
}

const TEMPLATES: Record<TemplateKey, TemplateOption> = {
    auditoria: {
        id: 'auditoria',
        title: '🏆 Auditoría de Oportunidades y Fuga de Pacientes',
        badge: 'Recomendada para Calificados',
        getSubject: (clinic) => `Auditoría y Plan de Recuperación de Pacientes — ${clinic}`,
        getBody: (clinic, doctor) => `Estimada/o ${doctor} y equipo de ${clinic},

Le escribo en seguimiento a la conversación que sostuvimos con su equipo de recepción a través de WhatsApp.

Durante el intercambio identificamos una situación habitual en clínicas con alto flujo: reciben numerosas consultas diarias de pacientes preguntando por precios, disponibilidad y tratamientos, pero una parte significativa de ellos no llega a concretar su cita debido a la falta de un seguimiento inmediato y continuo 24/7.

En Diana IA nos especializamos exclusivamente en apoyar a las recepciones de clínicas dentales para:

• Responder en segundos 24/7: Atención inmediata a cada interesado en WhatsApp, sin importar el día ni la hora.
• Asistir a recepción sin sustituir personal: Diana filtra las preguntas frecuentes, califica la necesidad del paciente y prepara la cita, liberando de saturación al equipo para que atiendan a los pacientes en sala.
• Recuperar pacientes y presupuestos: Diana reactiva cordialmente a quienes preguntaron y quedaron indecisos, recuperando citas que de otro modo se perderían.

Nos encantaría coordinar una breve llamada de 10 minutos esta semana para mostrarle cómo funciona y presentarle los resultados de nuestra auditoría.

También podemos habilitarles una prueba de 14 días sin costo ni compromiso para que evalúen directamente la recuperación de pacientes en su clínica.

Quedamos a su entera disposición.

Atentamente,

Abraham Acevedo
Director & Fundador | Diana IA
WhatsApp: +1 829-756-2014 · https://dianaia.online`,
    },
    ejecutivo: {
        id: 'ejecutivo',
        title: '⚡ Resumen Ejecutivo para Decisor',
        badge: 'Directo y conciso',
        getSubject: (clinic) => `Propuesta de Implementación Asistente IA — ${clinic}`,
        getBody: (clinic, doctor) => `Hola ${doctor},

Siguiendo la solicitud de su asistente en recepción, le comparto los puntos clave del sistema de inteligencia artificial Diana IA adaptado a ${clinic}:

1. Disponibilidad Total 24/7: Ningún paciente nuevo se queda sin respuesta fuera del horario laboral.
2. Apoyo al Equipo de Recepción: Diana atiende y clasifica las consultas en WhatsApp, agilizando el flujo diario de la clínica.
3. Cero Fuga de Consultas: Seguimiento automático a pacientes que solicitaron información o presupuestos y no confirmaron.

Podemos activar una prueba piloto de 14 días sin costo para que su equipo experimente el sistema en tiempo real.

¿Qué día de esta semana le resultaría conveniente para una demostración rápida de 10 minutos?

Saludos cordiales,

Abraham Acevedo
Director & Fundador | Diana IA
WhatsApp: +1 829-756-2014 · https://dianaia.online`,
    },
    activacion: {
        id: 'activacion',
        title: '🚀 Activación de Acceso y Demo en Vivo',
        badge: 'Prueba de 14 días',
        getSubject: (clinic) => `Acceso y Prueba Gratuita de 14 Días para ${clinic} — Diana IA`,
        getBody: (clinic, doctor) => `Estimada/o ${doctor},

Conforme a lo conversado con su equipo, hemos habilitado la opción de prueba gratuita de 14 días de Diana IA para ${clinic}.

Pasos para iniciar:
1. Ingrese a: https://dianaia.online/register
2. Vincule el WhatsApp de la clínica escaneando el código QR (toma menos de 1 minuto).
3. Diana comenzará a responder consultas y a registrar los pacientes atendidos.

Si prefiere que le asistamos en la configuración o coordinemos una sesión guiada de 10 minutos, respóndanos a este correo o escríbanos por WhatsApp.

Atentamente,

Equipo Diana IA
soporte@dianaia.online · https://dianaia.online`,
    },
};

const LeadEmailProposalModal: React.FC<Props> = ({ lead, onClose, onSent }) => {
    const defaultEmail = extractEmail(lead);
    const defaultDoctor = extractDoctorName(lead);
    const defaultClinic = lead.clinicName || 'Clínica Dental';
    const sentInfo = extractProposalSentInfo(lead);

    const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>('auditoria');
    const [recipientEmail, setRecipientEmail] = useState(defaultEmail);
    const [doctorName, setDoctorName] = useState(defaultDoctor);
    const [clinicName, setClinicName] = useState(defaultClinic);
    const [subject, setSubject] = useState(TEMPLATES.auditoria.getSubject(defaultClinic, defaultDoctor));
    const [body, setBody] = useState(TEMPLATES.auditoria.getBody(defaultClinic, defaultDoctor, lead.qualificationVolume));
    
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    // Update fields when template changes
    const handleSelectTemplate = (tplKey: TemplateKey) => {
        setSelectedTemplate(tplKey);
        const tpl = TEMPLATES[tplKey];
        setSubject(tpl.getSubject(clinicName, doctorName));
        setBody(tpl.getBody(clinicName, doctorName, lead.qualificationVolume));
    };

    const handleCopy = () => {
        const fullText = `Para: ${recipientEmail || '(sin correo)'}\nAsunto: ${subject}\n\n${body}`;
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        showToast('Asunto y cuerpo copiados al portapapeles', 'success');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleOpenMailto = () => {
        if (!recipientEmail) {
            showToast('Ingresa un correo destinatario primero', 'error');
            return;
        }
        const mailtoUrl = `mailto:${encodeURIComponent(recipientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoUrl, '_blank');
        showToast('Abriendo tu cliente de correo...', 'success');
    };

    const handleSendResend = async () => {
        if (!recipientEmail || !recipientEmail.includes('@')) {
            showToast('Ingresa una dirección de correo válida', 'error');
            return;
        }
        if (!subject.trim()) {
            showToast('El asunto no puede estar vacío', 'error');
            return;
        }
        if (!body.trim()) {
            showToast('El mensaje no puede estar vacío', 'error');
            return;
        }

        setSending(true);
        try {
            const cleanPhone = lead.phone.replace(/\D/g, '');
            const res = await api.post(`/marketing-sales/leads/${encodeURIComponent(cleanPhone)}/email-proposal`, {
                to: recipientEmail,
                subject,
                body,
                doctorName,
                clinicName,
            });

            if (res.data?.success) {
                showToast(`¡Correo enviado exitosamente a ${recipientEmail}!`, 'success');
                if (onSent) onSent(recipientEmail);
                setTimeout(() => onClose(), 1500);
            } else {
                showToast(res.data?.error || 'Error al enviar correo', 'error');
            }
        } catch (err: any) {
            console.error('Error sending proposal email:', err);
            showToast(err?.response?.data?.error || 'No se pudo enviar el correo vía Resend', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full sm:max-w-2xl bg-white dark:bg-slate-card rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col border border-slate-200 dark:border-slate-700 animate-slide-up">
                
                {/* Toast */}
                {toast && (
                    <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[300] text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 ${
                        toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
                    }`}>
                        <span className="material-symbols-outlined text-[16px]">
                            {toast.type === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {toast.msg}
                    </div>
                )}

                {/* Header */}
                <div className="flex-none p-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-indigo-50/80 via-white to-purple-50/40 dark:from-indigo-950/40 dark:via-slate-900/60 dark:to-purple-950/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-primary flex items-center justify-center text-white shadow-md shadow-indigo-500/20">
                                <span className="material-symbols-outlined text-[22px]">mark_email_read</span>
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    Plantilla de Propuesta por Correo
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                        Lead Calificado 🔥
                                    </span>
                                </h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {clinicName} · {lead.phone}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center justify-center"
                        >
                            <span className="material-symbols-outlined text-[18px]">close</span>
                        </button>
                    </div>

                    {/* Selector de Plantilla */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-none">
                        {(Object.keys(TEMPLATES) as TemplateKey[]).map(key => {
                            const tpl = TEMPLATES[key];
                            const isSelected = selectedTemplate === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => handleSelectTemplate(key)}
                                    className={`flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border text-left ${
                                        isSelected
                                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                                    }`}
                                >
                                    {tpl.title}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Banner de Aviso de Propuesta ya Enviada */}
                {sentInfo.sent && (
                    <div className="flex-none mx-4 mt-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 flex items-start gap-2.5 text-xs">
                        <span className="material-symbols-outlined text-[20px] text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5">mark_email_read</span>
                        <div className="flex-1">
                            <p className="font-bold text-emerald-950 dark:text-emerald-100 flex items-center gap-1.5">
                                ✓ Aviso: Plantilla de correo ya enviada a esta clínica
                            </p>
                            <p className="text-[11px] text-emerald-800/90 dark:text-emerald-300/90 mt-0.5 leading-relaxed">
                                {sentInfo.details}
                            </p>
                            <p className="text-[10px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">
                                Puedes volver a enviar una nueva copia o copiar el texto si necesitas reenviarlo.
                            </p>
                        </div>
                    </div>
                )}

                {/* Sub-header Tabs (Editor / Vista Previa) */}
                <div className="flex-none flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 text-xs">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setActiveTab('editor')}
                            className={`px-3 py-1 rounded-lg font-semibold transition-colors ${
                                activeTab === 'editor'
                                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            ✏️ Editar Contenido
                        </button>
                        <button
                            onClick={() => setActiveTab('preview')}
                            className={`px-3 py-1 rounded-lg font-semibold transition-colors ${
                                activeTab === 'preview'
                                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            👁️ Vista Previa HTML
                        </button>
                    </div>

                    <a
                        href="https://resend.com/emails"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] font-medium text-slate-500 hover:text-indigo-600 flex items-center gap-1"
                        title="Ir a consola de Resend"
                    >
                        <span>Resend Console</span>
                        <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                    </a>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3.5 text-xs">
                    {activeTab === 'editor' ? (
                        <>
                            {/* Inputs Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div>
                                    <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Para (Correo Destinatario) *
                                    </label>
                                    <div className="relative">
                                        <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[16px]">mail</span>
                                        <input
                                            type="email"
                                            value={recipientEmail}
                                            onChange={e => setRecipientEmail(e.target.value)}
                                            placeholder="ejemplo@clinicadental.com"
                                            className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                        />
                                    </div>
                                    {!recipientEmail && (
                                        <p className="text-[10px] text-amber-600 mt-0.5">
                                            ⚠️ Ingresa el correo proporcionado.
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Doctor / Decisor
                                    </label>
                                    <div className="relative">
                                        <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[16px]">person</span>
                                        <input
                                            type="text"
                                            value={doctorName}
                                            onChange={e => setDoctorName(e.target.value)}
                                            placeholder="Dra. Yudelkys Lorenzo"
                                            className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Clínica
                                    </label>
                                    <div className="relative">
                                        <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[16px]">domain</span>
                                        <input
                                            type="text"
                                            value={clinicName}
                                            onChange={e => setClinicName(e.target.value)}
                                            placeholder="Sodes Clínica Dental"
                                            className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Asunto del Correo
                                    </label>
                                    <input
                                        type="text"
                                        value={subject}
                                        onChange={e => setSubject(e.target.value)}
                                        className="w-full px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Textarea Body */}
                            <div>
                                <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center justify-between">
                                    <span>Cuerpo del Mensaje (Personalizable)</span>
                                    <span className="text-[10px] text-slate-400 font-normal">Soporta viñetas (•) y párrafos</span>
                                </label>
                                <textarea
                                    value={body}
                                    onChange={e => setBody(e.target.value)}
                                    rows={11}
                                    className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-mono leading-relaxed resize-y focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                />
                            </div>
                        </>
                    ) : (
                        /* Vista Previa HTML Renderizado */
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4 space-y-3">
                            <div className="bg-white dark:bg-slate-card rounded-xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 space-y-3">
                                <div className="border-b border-slate-100 dark:border-slate-700 pb-2.5">
                                    <p className="text-xs text-slate-500"><strong>Para:</strong> {recipientEmail || '(sin correo ingresado)'}</p>
                                    <p className="text-xs text-slate-800 dark:text-slate-200 font-bold mt-0.5"><strong>Asunto:</strong> {subject}</p>
                                </div>

                                <div className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed space-y-2">
                                    {body}
                                </div>

                                <div className="pt-3 border-t border-slate-100 dark:border-slate-700 text-center">
                                    <span className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold text-xs shadow-sm">
                                        Conocer más de Diana IA →
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex-none p-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-card flex flex-col sm:flex-row items-center justify-between gap-2.5">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            title="Copiar asunto y mensaje para pegarlo en cualquier parte"
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {copied ? 'check' : 'content_copy'}
                            </span>
                            {copied ? 'Copiado' : 'Copiar Texto'}
                        </button>

                        <button
                            type="button"
                            onClick={handleOpenMailto}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            title="Abrir en tu app de correo predeterminada (Gmail, Outlook)"
                        >
                            <span className="material-symbols-outlined text-[16px]">mail</span>
                            Abrir Mail
                        </button>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                        >
                            Cerrar
                        </button>

                        <button
                            type="button"
                            onClick={handleSendResend}
                            disabled={sending || !recipientEmail}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sending ? (
                                <>
                                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Enviando vía Resend...</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-[16px]">send</span>
                                    <span>Enviar Correo (Resend)</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default LeadEmailProposalModal;
